import { useEffect, useState, CSSProperties } from "react";

import AppSpinner from "../elements/AppSpinner";

interface PreviewProps {
  mode: string;
  value: any;
  type: string;
  extension?: string;
  hoverPreview?: boolean;
  modal?: boolean;
  styles?: CSSProperties;
}

const Preview = ({
  mode,
  value,
  type,
  extension,
  modal = false,
  styles,
}: PreviewProps) => {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (type !== "Text") return;
    let source: any = value.source;

    if (extension === "json") {
      setLoading(true);

      fetch(source)
        .then((res) => res.json())
        .then((data) => {
          setText(() =>
            typeof data === "object" ? JSON.stringify(data, undefined, 2) : data
          );
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(true);

      fetch(source)
        .then((res) => res.text())
        .then((data) => {
          setText(() =>
            typeof data === "object" ? JSON.stringify(data, undefined, 2) : data
          );
        })
        .finally(() => setLoading(false));
    }
  }, [value, type]);

  if (!value) return <div>--</div>;
  if (loading) return <AppSpinner />;

  return (
    <div
      className={`${
        modal && "modal-preview-container"
      } tw:flex tw:p-2 tw:justify-start tw:items-center tw:h-full tw:w-full`}
    >
      <HtmlTag
        type={type}
        value={value}
        mode={mode}
        text={text}
        styles={styles}
      />
    </div>
  );
};

interface HtmlTagProps {
  type: string;
  value: any;
  mode: string;
  text?: string | null;
  styles?: CSSProperties;
}

/**
 * HtmlTag component to render different types of media based on the provided type.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.type - The type of the media (e.g., "Image", "Video", "Audio", "Text", "PDF").
 * @param {Object} props.value - The value object containing media information.
 * @param {string} props.value.source - The source URL of the media.
 * @param {string} [props.value.Name] - The name of the media (optional, used for Video and Text types).
 * @param {string} props.mode - The mode in which the media is displayed (e.g., "table", "preview").
 * @param {string} [props.text] - The text content for Text type (optional).
 * @param {Object} [props.styles] - The styles object for the media element (optional).
 *
 * @returns {JSX.Element} The rendered media element.
 */
const HtmlTag = ({ type, value, mode, text, styles }: HtmlTagProps) => {
  let source = value.source;

  switch (type) {
    case "Image":
      return (
        <img
          src={source}
          alt="Attached"
          style={{
            objectFit: "contain",
            ...styles,
          }}
        />
      );
    case "Video":
      return mode === "table" ? (
        <div className="tw:truncate tw:text-dark">
          <span>
            <i
              className="mdi mdi-play-box tw:mr-2"
              style={{
                lineHeight: 1,
                fontSize: 20,
                position: "relative",
                top: 3,
              }}
            ></i>
            {value.Name}
          </span>
        </div>
      ) : (
        <video
          style={{
            height: "100%",
            width: "100%",
            objectFit: "contain",
            ...styles,
          }}
          controls
          autoPlay={mode === "preview" ? true : false}
        >
          <source src={value.source}></source>
        </video>
      );
    case "Audio":
      return mode === "table" ? (
        <audio
          controls
          style={{
            height: "35px",
            width: "100%",
            minWidth: "300px",
            paddingRight: "30px",
          }}
        >
          <source src={source}></source>
        </audio>
      ) : (
        <audio
          style={{
            width: "100%",
            height: "50px",
            minWidth: "300px",
            paddingRight: "30px",
            ...styles,
          }}
          controls
        >
          <source src={source}></source>
        </audio>
      );

    case "Text":
      return mode === "table" ? (
        <div className="tw:truncate tw:text-dark">
          <span>
            <i
              className="mdi mdi-play-box tw:mr-2"
              style={{
                lineHeight: 1,
                fontSize: 20,
                position: "relative",
                top: 3,
              }}
            ></i>
            {value.Name}
          </span>
        </div>
      ) : (
        <>
          {text ? (
            <pre
              className="tw:border tw:border-gray-300 tw:p-3"
              style={{
                height: "100%",
                width: "100%",
                overflowY: "scroll",
                overflowX: "scroll",
                fontSize: "1.7vh",
                whiteSpace: "pre-wrap",
                ...styles,
              }}
            >
              {text}
            </pre>
          ) : (
            <p>Loading...</p>
          )}
        </>
      );

    case "PDF":
      return (
        <object
          data={source}
          type="application/pdf"
          style={{ width: "100%", height: "100%" }}
          className="tw:flex tw:justify-center tw:items-center"
        >
          <a href={source} target="_blank">
            Open here
          </a>
        </object>
      );
    default:
      return (
        <div className="tw:flex tw:flex-col tw:items-center">
          <h2 className="tw:font-medium tw:m-0">File type not supported</h2>
          <h5 className="tw:font-medium tw:m-0">
            Click to open{" "}
            <a href={source} className="tw:text-primary" target="_blank">
              {value.Name}
            </a>
          </h5>
          <div className="tw:text-center tw:font-medium tw:mt-6 tw:pt-2">
            If you need to preview a certain file type,
            <br /> contact support
          </div>
        </div>
      );
  }
};

export default Preview;
